/**
 * TrendChart — Line chart showing SmartBlock score trend over time.
 * Uses recharts LineChart.
 */
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { TrendPoint } from '@/lib/smartblocksApi';

type TrendChartProps = {
  data: TrendPoint[];
  height?: number;
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function TrendChart({ data, height = 200 }: TrendChartProps) {
  const chartData = data.map((d) => ({
    ...d,
    dateLabel: formatDate(d.date),
    scorePercent: Math.round(d.gec0 * 100),
    momentumPercent: Math.round(d.momentum * 100),
  }));

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 py-12 text-sm text-cocoa/50">
        No data yet — submit your first block to see trends.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: '#78716c' }} />
        <YAxis
          domain={[0, 100]}
          tick={{ fontSize: 11, fill: '#78716c' }}
          tickFormatter={(v: number) => `${v}%`}
        />
        <Tooltip
          contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' }}
          formatter={(value: number, name: string) => [
            `${value}%`,
            name === 'scorePercent' ? 'Score' : 'Momentum',
          ]}
          labelFormatter={(label: string) => `Day: ${label}`}
        />
        <Line
          type="monotone"
          dataKey="scorePercent"
          stroke="#059669"
          strokeWidth={2}
          dot={{ r: 3, fill: '#059669' }}
          name="Score"
        />
        <Line
          type="monotone"
          dataKey="momentumPercent"
          stroke="#d97706"
          strokeWidth={1.5}
          dot={false}
          strokeDasharray="4 3"
          name="Momentum"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default TrendChart;
